import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import SitePageAnalysis from '../components/SitePageAnalysis';
import AssistantLayout from '../components/AssistantLayout';
import ScreenshotThumbnail from '../components/ScreenshotThumbnail';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { get_user_error_message } from '../utils/ErrorMessages';

// One page of a site. Opens on the page's latest finished analysis; Analyze runs a new one. Opened from AEO Analysis
// with state { analyze: true }, it starts an analysis as soon as the page has loaded, once.
function SitePageView(){
	const { token } = useAuth();
	const { id } = useParams();
	const location = useLocation();
	const navigate = useNavigate();

	const [site_page, set_site_page] = useState(null);
	const [latest_analysis, set_latest_analysis] = useState(null);
	const [error_message, set_error_message] = useState('');

	const site_page_analysis_ref = useRef(null);

	// Whether an analysis is still to be started on arrival from AEO Analysis.
	const analyze_on_load_ref = useRef(!!(location.state && location.state.analyze));

	useEffect(()=>{
		load_site_page();
	}, [id]);

	// Start the analysis asked for by AEO Analysis once the page is on screen. The request is cleared from the
	// history entry, so a refresh or going back does not start another.
	useEffect(()=>{
		if(!site_page || !analyze_on_load_ref.current || !site_page_analysis_ref.current){
			return;
		}

		analyze_on_load_ref.current = false;
		navigate(location.pathname, { replace: true, state: null });
		site_page_analysis_ref.current.send_form();
	}, [site_page]);

	const get_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	const load_site_page = async()=>{
		try {
			set_error_message('');

			const site_page_response = await axios.get(Constants.SERVER_URL + '/api/site-page/' + id, { headers: get_headers() });
			if(!site_page_response.data || !site_page_response.data.site_page){
				set_error_message('Page not found.');
				return;
			}

			const analysis_response = await axios.get(Constants.SERVER_URL + '/api/latest_site_page_analysis/' + id, { headers: get_headers() });
			set_latest_analysis(analysis_response.data ? analysis_response.data.analysis : null);
			set_site_page(site_page_response.data.site_page);
		}
		catch (error) {
			console.log('SitePageView/load_site_page - ERROR: ', describe_error(error));
			set_error_message(get_user_error_message(error, 'The page could not be loaded. Please try again.'));
		}
	};

	const on_analyze = (event)=>{
		event.preventDefault();

		if(site_page_analysis_ref.current){
			site_page_analysis_ref.current.send_form(event);
		}
	};

	// The thumbnail of the page's latest analysis, beside the title and url.
	const format_analysis_date = (date_value)=>{
		const date = new Date(date_value);
		return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
			' ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
	};

	// The date the latest finished analysis's page data is from, under the url.
	const render_analysis_date = ()=>{
		if(!latest_analysis || (latest_analysis.stage !== 'finished')){
			return null;
		}

		const page_data_date = latest_analysis.page_data_date ? latest_analysis.page_data_date : latest_analysis.run_date;
		return (
			<div className="site-analysis-date">
				{'Analysis date ' + format_analysis_date(page_data_date)}
			</div>
		);
	};

	const render_thumbnail = ()=>{
		if(!latest_analysis || !latest_analysis.screenshot_thumbnail_image_id){
			return null;
		}

		return (
			<Col xs="auto" className="site-page-thumbnail-col">
				<ScreenshotThumbnail image_id={latest_analysis.screenshot_thumbnail_image_id} alt_text="Screenshot of the page"/>
			</Col>
		);
	};

	const render_site_page = (on_analysis_change, on_ask_ai)=>{
		if(!site_page){
			return null;
		}

		return (
			<React.Fragment>
				<Row className="site-page-header">
					<Col xs="auto" className="site-page-heading">
						<h4 className="site-page-title">{site_page.title ? site_page.title : site_page.url}</h4>
						<a
							className="site-page-url"
							href={site_page.url}
							target="_blank"
							rel="noopener noreferrer">
							{site_page.url}
						</a>
						{render_analysis_date()}
					</Col>
					{render_thumbnail()}
					<Col xs="auto">
						<Button
							variant="primary"
							className="analyze-site-button"
							onClick={on_analyze}>
							Analyze
						</Button>
					</Col>
				</Row>

				<SitePageAnalysis
					enable_analysis_form={false}
					url_override={site_page.url}
					initial_analysis={latest_analysis}
					on_analysis_change={on_analysis_change}
					on_ask_ai={on_ask_ai}
					ref={site_page_analysis_ref}/>
			</React.Fragment>
		);
	};

	// The same centered column as the page analysis view; home-container clears the fixed navbar.
	const render_content = (on_analysis_change, on_ask_ai)=>{
		return (
			<div className="home-container analysis-view-container">
				{error_message && <Alert variant="danger">{error_message}</Alert>}
				{render_site_page(on_analysis_change, on_ask_ai)}
			</div>
		);
	};

	return (
		<AssistantLayout render_content={render_content} view_key={'site_page:' + id}/>
	);
}

export default SitePageView;
